import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { Home, Calculator, CalendarDays, ListChecks, LayoutGrid, Crown, Package, LogOut } from "lucide-react";
import { Toaster, toast } from "sonner";
import UpgradeModal from "./UpgradeModal";
import UpgradeButton from "./UpgradeButton";
import ManageProButton from "./ManageProButton";
import { useProState } from "../lib/pro";

const LOGO_SRC = "/popupprofit-logo.png";

const navItems = [
  { to: "/", label: "Home", short: "Home", icon: Home, testid: "nav-home" },
  { to: "/calculator", label: "Calculator", short: "Calc", icon: Calculator, testid: "nav-calculator" },
  { to: "/planner", label: "Planner", short: "Plan", icon: CalendarDays, testid: "nav-planner" },
  { to: "/products", label: "Products", short: "Products", icon: Package, testid: "nav-products" },
  { to: "/checklist", label: "Checklist", short: "List", icon: ListChecks, testid: "nav-checklist" },
  { to: "/dashboard", label: "Dashboard", short: "Stats", icon: LayoutGrid, testid: "nav-dashboard" },
];

export const Layout = () => {
  const { pro, email, logout } = useProState();
  const navigate = useNavigate();
  const isLoggedIn = !!email;

  const onSignOut = () => {
    logout();
    toast.success("Signed out");
    navigate("/");
  };

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      {/* Top bar */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/85 border-b border-brand-line no-print">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="brand-logo">
            <span className="w-10 h-10 rounded-xl bg-white border border-brand-line grid place-items-center overflow-hidden shadow-sm group-hover:shadow-md transition-shadow">
              <img src={LOGO_SRC} alt="PopUp Profit logo" className="w-9 h-9 object-contain" />
            </span>
            <span className="font-heading font-semibold text-brand-ink text-lg leading-none hidden xs:inline sm:inline">
              PopUp Profit<sup className="text-[10px] ml-0.5 text-brand-mute">™</sup>
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-1" aria-label="Primary">
            {navItems.map(({ to, label, icon: Icon, testid }) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/"}
                data-testid={`top-${testid}`}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-brand-green text-white shadow-sm"
                      : "text-brand-ink/80 hover:bg-brand-cream-soft"
                  }`
                }
              >
                <Icon className="w-4 h-4" strokeWidth={2} />
                {label}
              </NavLink>
            ))}
          </nav>
          <div className="shrink-0 flex items-center gap-2">
            {!isLoggedIn && (
              <>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center rounded-full border border-brand-green/40 text-brand-green hover:bg-brand-green/5 px-3 sm:px-4 py-2 text-sm font-medium transition-all active:scale-[0.97]"
                  data-testid="header-signin-btn"
                >
                  Sign In
                </Link>
                <Link
                  to="/signup"
                  className="inline-flex items-center justify-center rounded-full bg-brand-green text-white hover:bg-brand-green-hover px-3 sm:px-4 py-2 text-sm font-medium transition-all shadow-sm active:scale-[0.97]"
                  data-testid="header-signup-btn"
                >
                  Sign Up Free
                </Link>
              </>
            )}
            {isLoggedIn && pro && (
              <div className="flex items-center gap-2" data-testid="header-pro-active">
                <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-brand-gold/15 text-brand-gold border border-brand-gold/30 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide">
                  <Crown className="w-3.5 h-3.5" strokeWidth={2.5} /> Pro
                </span>
                <ManageProButton />
              </div>
            )}
            {isLoggedIn && (
              <button
                onClick={onSignOut}
                className="inline-flex items-center justify-center gap-1.5 rounded-full border border-brand-line text-brand-mute hover:text-brand-ink hover:border-brand-mute/40 hover:bg-brand-cream-soft px-3 sm:px-4 py-2 text-sm font-medium transition-all active:scale-[0.97]"
                data-testid="header-signout-btn"
                aria-label="Sign out"
              >
                <LogOut className="w-3.5 h-3.5" strokeWidth={2.25} />
                <span className="hidden sm:inline">Sign Out</span>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Page */}
      <main className="flex-1 w-full">
        <Outlet />
      </main>

      <Footer />

      {/* Bottom mobile nav */}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-white/95 backdrop-blur-md border-t border-brand-line pb-safe shadow-[0_-4px_20px_rgba(0,0,0,0.05)] no-print"
        aria-label="Mobile navigation"
      >
        <ul className="grid grid-cols-6">
          {navItems.map(({ to, short, icon: Icon, testid }) => (
            <li key={to}>
              <NavLink
                to={to}
                end={to === "/"}
                data-testid={`bottom-${testid}`}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10px] font-medium transition-colors ${
                    isActive ? "text-brand-green" : "text-brand-mute"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={`w-8 h-8 grid place-items-center rounded-full transition-colors ${
                        isActive ? "bg-brand-green/10" : ""
                      }`}
                    >
                      <Icon className="w-5 h-5" strokeWidth={2} />
                    </span>
                    {short}
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      <Toaster position="top-center" richColors />
      <UpgradeModal />
    </div>
  );
};

export const Footer = () => (
  <footer className="border-t border-brand-line bg-brand-cream-soft/60 mt-12 no-print" data-testid="site-footer">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 mb-16 md:mb-0 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
      <div className="flex items-center gap-2.5">
        <span className="w-8 h-8 rounded-lg bg-white border border-brand-line grid place-items-center overflow-hidden">
          <img src={LOGO_SRC} alt="PopUp Profit logo" className="w-7 h-7 object-contain" />
        </span>
        <div>
          <p className="text-sm text-brand-ink font-medium">
            © 2026 Verbum LLC. PopUp Profit<sup className="text-[10px] ml-0.5">™</sup> is operated by Verbum LLC.
          </p>
          <p className="text-xs text-brand-mute">All rights reserved.</p>
        </div>
      </div>
      <p className="text-xs text-brand-mute font-body">Know before you show.</p>
    </div>
  </footer>
);

export default Layout;
